import { bindActionCreators } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { BaseCartProduct, ProductDetailData } from '~shared-components/model'
import {
  closeCartModal as closeModalAction,
  openCartModal as openModalAction,
} from '~shared-components/redux/actions/cart-modal.actions'
import { CartModalState } from '~shared-components/redux/reducers/cart-modal.reducers'
import { CartModalSelector } from '~shared-components/redux/selectors/cart-modal.selector'
import { LoginSelector } from '~shared-components/redux/selectors/login.selector'
import { transformProductToBaseCartProduct } from '~shared-components/transforms/product.transforms'

import { useCart } from './useCart'

export const useCartModal = () => {
  const modalState = useSelector(CartModalSelector.cartModal)
  const customerContext = useSelector(LoginSelector.selectedCustomerContext)
  const dispatch = useDispatch()

  const { findProductInCart, isCartLocked } = useCart()

  const { closeCartModal, openCartModalInternal } = bindActionCreators(
    {
      closeCartModal: closeModalAction,
      openCartModalInternal: openModalAction,
    },
    dispatch,
  )

  const openCartModal = useCallback(
    (product: ProductDetailData, type: CartModalState['type']) => {
      const getExistingProduct = (
        product: ProductDetailData,
      ): BaseCartProduct => {
        const existingProduct = findProductInCart(product.id)
        const quantity = existingProduct?.quantity ?? product.quantity
        return transformProductToBaseCartProduct(
          product,
          customerContext!,
          quantity,
        )
      }

      openCartModalInternal({
        product: getExistingProduct(product),
        type,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerContext],
  )

  const addProductToCart = useCallback(
    (product: ProductDetailData) => {
      if (isCartLocked(product.pricePoints?.[0]?.price.currency)) return

      openCartModal(product, 'cart')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openCartModal],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const closeCartModalExported = useCallback(() => closeCartModal(), [])

  const requestQuoteForProduct = useCallback(
    (product: ProductDetailData) => openCartModal(product, 'quote'),
    [openCartModal],
  )

  return {
    ...modalState,
    addProductToCart,
    closeAddProductToCart: closeCartModalExported,
    closeRequestQuoteForProduct: closeCartModalExported,
    findProductInCart,
    requestQuoteForProduct,
  }
}
